
import SimpleUri from '@ms/odsp-utilities/lib/uri/SimpleUri';

import { IUrlParts } from './IUrlHelper';

/**
* Backing implementation of the result from a call to UrlHelper.getUrlParts.
* This class relies heavily on lazy-initialization of values in order to improve performance.
*
* @class UrlParts
* @implements {IUrlParts}
*/
class UrlParts implements IUrlParts {

    public get endpoint(): string | undefined {
        return this._getEndpoint();
    }

    public get driveId(): string | undefined {
        return this._getDriveId();
    }

    public get itemId(): string | undefined {
        return this._getItemId();
    }

    private _url: string;

    constructor(url: string) {
        this._url = url;
    }

    private _getEndpoint(): string | undefined {
        let endpoint: string | undefined;

        const indexOfDrives = this._url.indexOf('/drives');
        if (indexOfDrives > -1) {
            endpoint = this._url.substring(0, indexOfDrives);

            if (endpoint.indexOf('https:') === 0) {
                // ensure the port isn't in the endpoint url
                endpoint = endpoint.replace(':443/', '/');
            }
        }

        this._getEndpoint = () => endpoint;

        return endpoint;
    }

    private _getDriveId(): string | undefined {
        const indexOfDrivesSegment = this._getIndexOfDrivesSegment();

        let driveId: string | undefined;

        if (indexOfDrivesSegment > -1) {
            const pathSegments = this._getPathSegments();

            driveId = pathSegments[indexOfDrivesSegment + 1];
        }

        this._getDriveId = () => driveId;

        return driveId;
    }

    private _getItemId(): string | undefined {
        const indexOfItemsSegment = this._getIndexOfItemsSegment();

        let itemId: string | undefined;

        if (indexOfItemsSegment > -1) {
            const pathSegments = this._getPathSegments();

            itemId = pathSegments[indexOfItemsSegment + 1];

            const queryParamIndex = itemId ? itemId.indexOf("?") : -1;
            if (queryParamIndex !== -1) {
                // the item id in the url might have ?version=Published on the end of it
                // strip it out
                itemId = itemId.substring(0, queryParamIndex);
            }
        } else {
            itemId = 'root';
        }

        this._getItemId = () => itemId;

        return itemId;
    }

    private _getPathSegments(): string[] {
        const url = this._url;

        let pathSegments: string[];

        if (url) {
            pathSegments = new SimpleUri(url).segments;
        } else {
            pathSegments = [];
        }

        this._getPathSegments = () => pathSegments;

        return pathSegments;
    }

    private _getIndexOfDrivesSegment(): number {
        const pathSegments = this._getPathSegments();

        const indexOfDrivesSegment = pathSegments.indexOf('drives');

        this._getIndexOfDrivesSegment = () => indexOfDrivesSegment;

        return indexOfDrivesSegment;
    }

    private _getIndexOfItemsSegment(): number {
        const pathSegments = this._getPathSegments();

        const indexOfItemsSegment = pathSegments.indexOf('items');

        this._getIndexOfItemsSegment = () => indexOfItemsSegment;

        return indexOfItemsSegment;
    }
}

/**
 * Extracts the available URL parts for an item given available information.
 * Any or all of the inputs may be provided, though the outputs may be undefined
 * if insufficient information is provided.
 */
export function getUrlParts(url: string): IUrlParts {
    return new UrlParts(url);
}
